import { UserIcon } from "lucide-react";
import React from "react";

const typeLabels = {
    identity: "Identity",
    business: "Business",
    addressProof: "Address Proof",
};

const statusStyles = {
    "1": "bg-yellow-100 text-yellow-700",
    "2": "bg-green-100 text-green-700",
    "3": "bg-red-100 text-red-700",
};

export default function VerificationRequestTable({
    verifications,
    getStatusInfo,
    setCurrentUserInfo,
    setOpen,
    setClickedItem,
}) {
    return (
        <div>
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Request ID
                        </th>
                        <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Professional
                        </th>
                        <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type
                        </th>
                        <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                        </th>
                        <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Priority
                        </th>
                        <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Submitted
                        </th>
                        <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                        </th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {verifications?.map((request: any) => {
                        // Backend now returns flattened requests
                        const statusId = request.status?.id || request.status?._id || "1";
                        const statusText = statusId === "1" ? "Pending" : statusId === "2" ? "Approved" : "Rejected";
                        const statusClassName = statusStyles[statusId] || statusStyles["1"];

                        return (
                            <tr key={request.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="text-sm font-medium text-gray-900">{request.verificationId}</span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                        <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                                            {request.userId?.photo?.path ? (
                                                <>
                                                    <img
                                                        src={request.userId.photo.path}
                                                        alt={`${request.userId.firstName} avatar`}
                                                        className="h-10 w-10 rounded-full object-cover"
                                                        onError={(e) => {
                                                            // If the profile photo fails to load, hide the img and show User icon
                                                            e.currentTarget.style.display = "none";
                                                            const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                                                            if (nextElement) {
                                                                nextElement.style.display = "flex";
                                                            }
                                                        }}
                                                    />
                                                    <div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ display: "none" }}>
                                                        <UserIcon className="h-5 w-5 text-gray-400" />
                                                    </div>
                                                </>
                                            ) : (
                                                <UserIcon className="h-5 w-5 text-gray-400" />
                                            )}
                                        </div>
                                        <div className="ml-4">
                                            <div className="font-medium text-gray-900">
                                                {request.userId?.firstName} {request.userId?.lastName}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                {request.userId?.role?.id === "4"
                                                    ? "Professional"
                                                    : "User"}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize bg-blue-100 text-blue-800 hover:bg-blue-200">
                                        {typeLabels[request.requestType] || request.requestType}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div
                                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors ${statusClassName}`}>
                                        {statusText}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold bg-red-100 text-red-700">
                                        High
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {new Date(request.createdAt).toLocaleDateString()}
                                </td>
                                <td className="py-4 whitespace-nowrap text-right text-sm font-medium flex">
                                    <button
                                        className="inline-flex items-center justify-center rounded-md text-sm font-medium
                                            transition-colors focus-visible:outline-none focus-visible:ring-2
                                            focus-visible:ring-blue-500 disabled:pointer-events-none disabled:opacity-50
                                            hover:bg-gray-100 h-8 px-3 text-blue-600 hover:text-blue-700"
                                        onClick={() => {
                                            // Create a verification object for the modal with the old structure
                                            // Initialize all file types to prevent undefined errors
                                            const verification = {
                                                id: request.verificationId,
                                                userId: request.userId,
                                                identityFiles: [],
                                                businessFiles: [],
                                                addressProofFiles: [],
                                                identityStatus: null,
                                                businessStatus: null,
                                                addressProofStatus: null,
                                                identityRemarks: "",
                                                businessRemarks: "",
                                                addressProofRemarks: "",
                                                identityNotes: [],
                                                businessNotes: [],
                                                addressProofNotes: [],
                                                createdAt: request.createdAt,
                                                updatedAt: request.updatedAt,
                                                // Override with actual data for the specific request type
                                                [`${request.requestType}Files`]: request.files,
                                                [`${request.requestType}Status`]: request.status,
                                                [`${request.requestType}Remarks`]: request.remarks || "",
                                                [`${request.requestType}Notes`]: request.notes || [],
                                            };
                                            setCurrentUserInfo(verification);
                                            setOpen(true);
                                            setClickedItem(`${request.requestType}Files`);
                                        }}>
                                        View Details
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
